/**
 * Emoji detection for CANmail content warnings.
 *
 * Emoji frequently fail to render in Outlook (and some other email clients),
 * showing up as empty boxes or missing glyphs. The composer warns before sending
 * when it finds any, so this module answers "does this content contain emoji, and
 * which ones?".
 */

/**
 * Characters that are technically pictographic/emoji but render reliably as plain
 * text in every client and appear constantly in legitimate email footers. Excluded
 * so a footer's "(c) 2026 Company" doesn't trip the warning.
 */
const BENIGN_PICTOGRAPHIC = new Set(['©', '®', '™']); // (c) (r) (tm)

/**
 * A single emoji-ish code point: a colour pictograph, or a regional-indicator
 * letter (the halves of a flag). Regional indicators are matched by their
 * code-point range rather than `\p{Regional_Indicator}`, which ECMAScript regex
 * does not recognise as a property name.
 */
const EMOJI_CHAR = /\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]/u;

/** Variation selector (U+FE0F) and zero-width joiner (U+200D) used in sequences. */
const VARIATION_SELECTOR = '️';
const ZERO_WIDTH_JOINER = '‍';

/** Strip tags so we scan what the recipient actually reads, not the markup. */
function toText(html: string): string {
    return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ');
}

/** True when a grapheme is a warning-worthy emoji (and not a benign (c)/(r)/(tm)). */
function isEmojiSegment(segment: string): boolean {
    if (!EMOJI_CHAR.test(segment)) {
        return false;
    }
    // A lone benign symbol (optionally with a variation selector) is not an emoji
    // worth warning about.
    const stripped = Array.from(segment).filter((ch) => ch !== VARIATION_SELECTOR);
    if (stripped.length > 0 && stripped.every((ch) => BENIGN_PICTOGRAPHIC.has(ch))) {
        return false;
    }
    return true;
}

/**
 * Return the distinct emoji found in a chunk of email content (HTML or plain
 * text), in first-seen order. Empty array when there are none.
 *
 * Emoji assembled from sequences — skin-tone modifiers, ZWJ families, flags — are
 * grouped so each counts once, using `Intl.Segmenter` where available and falling
 * back to a sequence-aware regex otherwise.
 */
export function detectEmojis(input?: string | null): string[] {
    if (!input || typeof input !== 'string') {
        return [];
    }
    const text = toText(input);
    if (!EMOJI_CHAR.test(text)) {
        return [];
    }

    const found: string[] = [];
    const seen = new Set<string>();
    const add = (segment: string): void => {
        if (isEmojiSegment(segment) && !seen.has(segment)) {
            seen.add(segment);
            found.push(segment);
        }
    };

    // Prefer grapheme segmentation so ZWJ sequences / skin tones group as one glyph.
    const SegmenterCtor =
        typeof Intl !== 'undefined'
            ? (Intl as {
                  Segmenter?: new (
                      ...args: unknown[]
                  ) => { segment: (s: string) => Iterable<{ segment: string }> };
              }).Segmenter
            : undefined;
    if (typeof SegmenterCtor === 'function') {
        const segmenter = new SegmenterCtor(undefined, { granularity: 'grapheme' });
        for (const { segment } of segmenter.segment(text)) {
            add(segment);
        }
        return found;
    }

    // Fallback: pair regional indicators into flags, and greedily group a pictograph
    // with any following variation selectors / skin-tone modifiers / ZWJ pictographs.
    const sequence = new RegExp(
        '[\\u{1F1E6}-\\u{1F1FF}][\\u{1F1E6}-\\u{1F1FF}]' +
            '|\\p{Extended_Pictographic}' +
            `(?:${VARIATION_SELECTOR}|[\\u{1F3FB}-\\u{1F3FF}]|${ZERO_WIDTH_JOINER}\\p{Extended_Pictographic})*`,
        'gu'
    );
    const matches = text.match(sequence) || [];
    matches.forEach(add);
    return found;
}

/** Convenience boolean: does this content contain any warning-worthy emoji? */
export function hasEmojis(input?: string | null): boolean {
    return detectEmojis(input).length > 0;
}

export default detectEmojis;
